/**
 * Hook to fetch and cache form API configuration
 * This replaces static field configurations with dynamic ones from the API
 */

import { useState, useEffect } from 'react';
import { FormApiResponse } from '../../utils/formApiConfigExtractor';
import { getCookie } from '../../utils/helpers';

// Cache for form API configuration
let formApiConfigCache: FormApiResponse | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get authentication headers for API requests
 * Follows the same pattern as other API calls in the application
 */
const getAuthHeaders = () => {
  const token = getCookie('app_access_token');
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
};

/**
 * Fetches form API configuration from the server
 */
const fetchFormApiConfig = async (): Promise<FormApiResponse> => {
  // Check if we have valid cached data
  const now = Date.now();
  if (formApiConfigCache && now - cacheTimestamp < CACHE_DURATION) {
    return formApiConfigCache;
  }

  try {
    // Use the correct form API endpoint
    const API_BASE_URL =
      process.env.REACT_APP_API_V2_URL ||
      (process.env.NODE_ENV === 'development'
        ? 'http://localhost:8080/product-roadmap/api/v2'
        : '/product-roadmap/api/v2');

    const response = await fetch(`${API_BASE_URL}/templates/rsd/form`, {
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch form API config: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();

    // Update cache - the API returns an array, take the first element
    formApiConfigCache = Array.isArray(data) ? data[0] : data;
    cacheTimestamp = now;

    return formApiConfigCache!; // We know it's not null since we just assigned it
  } catch (error) {
    console.error('Error fetching form API config:', error);

    // Return fallback configuration if API fails
    return getFallbackFormApiConfig();
  }
};

/**
 * Minimal fallback configuration for graceful degradation
 */
const getFallbackFormApiConfig = (): FormApiResponse => {
  console.warn('Using fallback form API config - some features may be limited');
  return {
    id: 'fallback',
    types: {
      'Product Release with Marketing': {
        dialogSize: 'large',
        sections: [
          {
            heading: 'Overview',
            subheading: '',
            fields: []
          }
        ]
      }
    }
  };
};

/**
 * Hook to get form API configuration
 */
export const useFormApiConfig = () => {
  const [config, setConfig] = useState<FormApiResponse | null>(
    formApiConfigCache
  );
  const [loading, setLoading] = useState<boolean>(!formApiConfigCache);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        setLoading(true);
        setError(null);
        const formConfig = await fetchFormApiConfig();
        setConfig(formConfig);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to load form configuration'
        );
        // Use fallback config on error
        setConfig(getFallbackFormApiConfig());
      } finally {
        setLoading(false);
      }
    };

    // Only load if we don't have cached config
    if (!formApiConfigCache) {
      loadConfig();
    }
  }, []);

  return {
    config,
    loading,
    error,
    refetch: () => {
      // Clear cache and refetch
      formApiConfigCache = null;
      cacheTimestamp = 0;
      setLoading(true);
      fetchFormApiConfig()
        .then(setConfig)
        .finally(() => setLoading(false));
    }
  };
};

/**
 * Synchronous function to get cached form API config
 * Use this when you're sure the config is already loaded
 */
export const getCachedFormApiConfig = (): FormApiResponse | null => {
  return formApiConfigCache;
};
